import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Card, message, Typography, Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../../components/shared/PageHeader';
import {
  listWorkspaceSessions, createWorkspaceSession, deleteWorkspaceSession,
  WorkspaceSession,
} from '../../services/workspaceApi';

const isIwmConfigError = (e: unknown): string | null => {
  const err = e as { response?: { status?: number; data?: { message?: string } } };
  const status = err?.response?.status;
  const msg = err?.response?.data?.message || '';
  if (
    status === 503 ||
    status === 502 ||
    /iwm|not configured|workspace.?manager|unreachable/i.test(msg)
  ) {
    return msg || 'Infra Workspace Manager (IWM) is not configured or unreachable.';
  }
  return null;
};

const errMsg = (e: unknown, fallback: string) => {
  const err = e as { errorFields?: unknown; response?: { data?: { message?: string } } };
  if (err?.errorFields) return null;
  return err?.response?.data?.message || fallback;
};

const WorkspaceSessionsPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const role = useSelector((s: RootState) => s.auth.user?.role);
  const canWrite = role === 'itadmin' || role === 'superadmin';

  const [rows, setRows] = useState<WorkspaceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [iwmError, setIwmError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    setIwmError(null);
    try {
      const data = await listWorkspaceSessions();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const iwm = isIwmConfigError(e);
      if (iwm) {
        setIwmError(iwm);
        setRows([]);
      } else {
        message.error(errMsg(e, 'Failed to load sessions') || 'Failed to load sessions');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await createWorkspaceSession({
        user: values.user.trim(),
        profileId: values.profileId.trim(),
      });
      message.success('Session created');
      setModalOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      const iwm = isIwmConfigError(e);
      if (iwm) {
        setIwmError(iwm);
        message.error(iwm);
      } else {
        const m = errMsg(e, 'Create failed');
        if (m) message.error(m);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record: WorkspaceSession) => {
    Modal.confirm({
      title: 'Delete session?',
      content: `Stop and remove session for ${record.user} (${record.id})?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteWorkspaceSession(record.id);
          message.success('Session deleted');
          await load();
        } catch (e) {
          const iwm = isIwmConfigError(e);
          message.error(iwm || errMsg(e, 'Delete failed') || 'Delete failed');
        }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 180, ellipsis: true },
    { title: 'User', dataIndex: 'user', key: 'user', width: 140 },
    { title: 'Profile', dataIndex: 'profileId', key: 'profileId', width: 140 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'noVNC',
      dataIndex: 'novncUrl',
      key: 'novncUrl',
      ellipsis: true,
      render: (url: string) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open
          </a>
        ) : (
          '—'
        ),
    },
    canWrite
      ? {
          title: 'Actions',
          key: 'actions',
          width: 100,
          render: (_: unknown, record: WorkspaceSession) => (
            <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
              Delete
            </Button>
          ),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="Workspace Sessions"
            purpose="Operator view of IWM workspace sessions — create and tear down pods."
            actions={
              canWrite ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }} disabled={!!iwmError}>
                  Create Session
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Typography.Title level={4} className="!mb-0">
              Workspace Sessions
            </Typography.Title>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }} disabled={!!iwmError}>
                Create Session
              </Button>
            )}
          </div>
        )}

        {iwmError && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="IWM not configured"
            description={iwmError}
            action={
              <Button size="small" onClick={load}>
                Retry
              </Button>
            }
          />
        )}

        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns as any}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title="Create Workspace Session"
        open={modalOpen}
        onOk={handleCreate}
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        okText="Create"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="user" label="User" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="IPA / POSIX username" />
          </Form.Item>
          <Form.Item name="profileId" label="Profile ID" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="IWM profile id" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkspaceSessionsPage;
